import { createRoot } from 'react-dom/client';
import { Theme } from '@classify/react-core-components/Theme';
import type { ClientApi } from '@openclassrooms/sdk-web';
import ExpandableEmbeddedChapter from '../components/ExpandableEmbeddedChapter';
import type { EmbeddedChapterTranslations, ParseRichContent } from '../types';
import { getEmbeddedChapterKey } from './expandableEmbeddedChapter';
const CHAPTER_PATH_WITH_DIRECT_SEGMENT_REGEXP = /^\/(?:[a-z]{2}\/)?courses\/(\d+)(?:-[^/]+)?\/(\d+)(?:-[^/]+)?\/?$/;
const CHAPTER_PATH_WITH_NAMED_SEGMENT_REGEXP = /^\/(?:[a-z]{2}\/)?courses\/(\d+)(?:-[^/]+)?\/chapters\/(\d+)(?:-[^/]+)?\/?$/;
const CHAPTER_INLINE_EXPANSION_ATTR = 'data-chapter-inline-expansion';
const CHAPTER_INLINE_CONTENT_ATTR = 'data-chapter-inline-content';
const parseEmbeddedChapterTranslations = (domContainer: HTMLElement): EmbeddedChapterTranslations => {
  const {
    embeddedChapterTranslations
  } = domContainer.dataset;
  if (!embeddedChapterTranslations) {
    return {};
  }
  try {
    return JSON.parse(embeddedChapterTranslations) as EmbeddedChapterTranslations;
  } catch {
    return {};
  }
};
type EmbeddedChapterRef = {
  courseId: number;
  chapterId: number;
};
export const extractChapterIdentifiersFromUrl = (url: URL): {
  courseId: number;
  chapterId: number;
} | null => {
  const directMatch = url.pathname.match(CHAPTER_PATH_WITH_DIRECT_SEGMENT_REGEXP);
  if (directMatch) {
    return {
      courseId: Number(directMatch[1]),
      chapterId: Number(directMatch[2])
    };
  }
  const namedMatch = url.pathname.match(CHAPTER_PATH_WITH_NAMED_SEGMENT_REGEXP);
  if (namedMatch) {
    return {
      courseId: Number(namedMatch[1]),
      chapterId: Number(namedMatch[2])
    };
  }
  return null;
};
const isInsideChapterInlineContainer = (linkElement: HTMLAnchorElement): boolean => Boolean(linkElement.closest(`[${CHAPTER_INLINE_EXPANSION_ATTR}]`) || linkElement.closest(`[${CHAPTER_INLINE_CONTENT_ATTR}]`));
const removeDuplicateLink = (linkElement: HTMLAnchorElement): void => {
  const parentElement = linkElement.parentElement;
  linkElement.remove();
  if (parentElement && parentElement.tagName === 'P' && (parentElement.textContent ?? '').trim() === '') {
    parentElement.remove();
  }
};
const isStandaloneLink = (linkElement: HTMLAnchorElement): boolean => {
  const parentElement = linkElement.parentElement;
  if (!parentElement) {
    return false;
  }
  const parentText = (parentElement.textContent ?? '').trim();
  const linkText = (linkElement.textContent ?? '').trim();
  const linkCountInParent = parentElement.querySelectorAll('a').length;
  return linkCountInParent === 1 && parentText.length > 0 && parentText === linkText;
};
function renderChapterInlineLink(linkElement: HTMLAnchorElement, chapterIdentifiers: {
  courseId: number;
  chapterId: number;
}, client: ClientApi, previousEmbeddedChapters: EmbeddedChapterRef[] = [], translations: EmbeddedChapterTranslations = {}, parseRichContent?: ParseRichContent) {
  const parentElement = linkElement.parentElement;
  const isStandalone = isStandaloneLink(linkElement);
  const shouldReplaceParent = Boolean(parentElement && parentElement.tagName === 'P' && isStandalone);
  const rootContainer = document.createElement('div');

  // Standalone link in <p>: replace entire paragraph
  if (shouldReplaceParent && parentElement?.parentNode) {
    parentElement.parentNode.replaceChild(rootContainer, parentElement);
  }
  // Inline link within sentence: insert after paragraph, remove link only
  else if (parentElement?.parentNode) {
    parentElement.parentNode.insertBefore(rootContainer, parentElement.nextSibling);
    linkElement.remove();
  }
  // Fallback: link has no paragraph parent, replace link directly
  else if (linkElement.parentNode) {
    linkElement.parentNode.replaceChild(rootContainer, linkElement);
  }
  // No valid parent node found
  else {
    return;
  }
  const root = createRoot(rootContainer);
  root.render(<Theme>
      <ExpandableEmbeddedChapter client={client} href={linkElement.href} linkLabel={linkElement.textContent || linkElement.href} courseId={chapterIdentifiers.courseId} chapterId={chapterIdentifiers.chapterId} previousEmbeddedChapters={previousEmbeddedChapters} translations={translations} parseRichContent={parseRichContent} />
    </Theme>);
}
const getEmbeddedChapters = (domContainer: HTMLElement): EmbeddedChapterRef[] => {
  const embeddedChapterOrder: EmbeddedChapterRef[] = [];
  const seenChapterKeys = new Set<string>();
  const links = Array.from(domContainer.getElementsByTagName('a'));
  links.forEach(linkElement => {
    if (isInsideChapterInlineContainer(linkElement)) {
      return;
    }
    const chapterIdentifiers = extractChapterIdentifiersFromUrl(new URL(linkElement.href));
    if (!chapterIdentifiers) {
      return;
    }
    const chapterKey = getEmbeddedChapterKey(chapterIdentifiers);
    if (seenChapterKeys.has(chapterKey)) {
      return;
    }
    seenChapterKeys.add(chapterKey);
    embeddedChapterOrder.push(chapterIdentifiers);
  });
  return embeddedChapterOrder;
};
export default function handleChapterEmbedding(domContainer: HTMLElement, client?: ClientApi, parseRichContent?: ParseRichContent) {
  if (!client || domContainer.dataset.embeddedChapterEnabled !== 'true') {
    return null;
  }
  const links = Array.from(domContainer.getElementsByTagName('a'));
  const embeddedChapters = getEmbeddedChapters(domContainer);
  const renderedChapterKeys = new Set<string>();
  const embeddedChapterTranslations = parseEmbeddedChapterTranslations(domContainer);
  if (!links.length) {
    return null;
  }
  links.forEach(linkElement => {
    if (isInsideChapterInlineContainer(linkElement)) {
      return;
    }
    const chapterIdentifiers = extractChapterIdentifiersFromUrl(new URL(linkElement.href));
    if (!chapterIdentifiers) {
      return;
    }
    const chapterKey = getEmbeddedChapterKey(chapterIdentifiers);
    if (renderedChapterKeys.has(chapterKey)) {
      if (isStandaloneLink(linkElement)) {
        removeDuplicateLink(linkElement);
      }
      // if the duplicate link is not standalone, we keep the text + link as is
      return;
    }
    renderedChapterKeys.add(chapterKey);
    const chapterIndex = embeddedChapters.findIndex(embeddedChapter => embeddedChapter.courseId === chapterIdentifiers.courseId && embeddedChapter.chapterId === chapterIdentifiers.chapterId);
    const previousEmbeddedChapters = chapterIndex > 0 ? embeddedChapters.slice(0, chapterIndex) : [];
    renderChapterInlineLink(linkElement, chapterIdentifiers, client, previousEmbeddedChapters, embeddedChapterTranslations, parseRichContent);
  });
  return null;
}